import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatDateTime } from '../../utils/timezoneUtils';

interface TicketReply {
  id: number;
  ticket_id: number;
  sender: 'client' | 'admin';
  message: string;
  attachment: {
    path: string;
    filename: string;
  } | null;
  created_at: string;
}

interface TicketDetail {
  id: number;
  status: string;
  created_at: string;
  updated_at: string;
  request: {
    id: number;
    number: string;
    company_name: string;
    package_name: string;
    country_name: string;
    country_flag: string;
  };
}

interface TicketResponse {
  ticket: TicketDetail;
  replies: TicketReply[];
}

const TicketDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { } = useUserAuth();
  const { ticketId } = useParams<{ ticketId: string }>();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyLoading, setReplyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetail();
    }
  }, [ticketId]);

  const getCountryFlagImage = (countryFlag: string, countryName: string) => {
    const isEmoji = countryFlag && countryFlag.length > 2;
    const fallbackEmoji = isEmoji ? countryFlag : '🌍';
    const countryCode = isEmoji ? '' : countryFlag.toUpperCase();

    if (countryCode && countryCode.length <= 3) {
      return (
        <img
          src={`https://flagcdn.com/48x36/${countryCode.toLowerCase()}.png`}
          alt={`${countryName} flag`}
          className="w-5 h-3 object-cover rounded-sm inline"
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            const parent = target.parentNode as HTMLElement;
            if (parent && !parent.querySelector('.flag-fallback')) {
              const span = document.createElement('span');
              span.className = 'flag-fallback text-lg';
              span.textContent = fallbackEmoji;
              parent.appendChild(span);
            }
          }}
        />
      );
    }

    return <span className="text-lg">{fallbackEmoji}</span>;
  };

  const fetchTicketDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://patrixglobal.com/backend/api/tickets/get.php?id=${ticketId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch ticket detail');
      }

      const data = await response.json();
      if (data.success) {
        setTicket(data.ticket);
        setReplies(data.replies || []);
      } else {
        setError(data.error || 'Failed to load ticket detail');
      }
    } catch (error) {
      setError('Error loading ticket detail');    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!replyMessage.trim() && !replyFile) {
      return;
    }

    try {
      setReplyLoading(true);
      
      let requestBody;
      let requestHeaders: any = {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      };

      if (replyFile) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('ticket_id', ticketId!);
        formData.append('message', replyMessage);
        formData.append('attachment', replyFile);
        requestBody = formData;
      } else {
        // Use JSON for text-only replies
        requestHeaders['Content-Type'] = 'application/json';
        requestBody = JSON.stringify({
          ticket_id: ticketId,
          message: replyMessage
        });
      }

      const response = await fetch('https://patrixglobal.com/backend/api/tickets/get.php', {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      const data = await response.json();
      if (data.message) {
        setReplyMessage('');
        setReplyFile(null);
        // Reset file input
        const fileInput = document.getElementById('reply-file') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        
        // Refresh ticket detail to show new reply
        await fetchTicketDetail();
      } else {
        setError(data.error || 'Failed to send reply');
      }
    } catch (error) {
      setError('Error sending reply');    } finally {
      setReplyLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'waiting_for_client': 'bg-yellow-100 text-yellow-800',
      'answered': 'bg-green-100 text-green-800',
      'processing': 'bg-blue-100 text-blue-800',
      'closed': 'bg-gray-100 text-gray-800',
    };

    const statusTranslationMap = {
      'waiting_for_client': 'waitingForClient',
      'answered': 'answered',
      'processing': 'processing',
      'closed': 'closed',
    };

    const normalizedStatus = status.toLowerCase();
    const translationKey = statusTranslationMap[normalizedStatus as keyof typeof statusTranslationMap] || normalizedStatus;
    const cssClass = statusClasses[normalizedStatus as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cssClass}`}>
        {t(`dashboard.tickets.${translationKey}`) || status}
      </span>
    );
  };

  const formatDateDisplay = (dateString: string) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, locale);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        e.target.value = '';
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only images (JPG, PNG, GIF), PDFs, and Word documents are allowed');
        e.target.value = '';
        return;
      }
      
      setReplyFile(file);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to="/dashboard/tickets"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Tickets
          </Link>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error || 'Ticket not found'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4">
          <Link
            to="/dashboard/tickets"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Tickets
          </Link>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Ticket #{ticket.id}
            </h1>
            <p className="mt-2 text-gray-600">
              {ticket.request.number} - {ticket.request.package_name}
            </p>
            <p className="text-sm text-gray-500">
              {getCountryFlagImage(ticket.request.country_flag, ticket.request.country_name)} {ticket.request.country_name}
              {ticket.request.company_name && ` • ${ticket.request.company_name}`}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {getStatusBadge(ticket.status)}
            <div className="text-right">
              <p className="text-sm text-gray-500">Created</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDateDisplay(ticket.created_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Conversation */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                {t('dashboard.tickets.conversation')}
              </h2>
            </div>
            
            <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
              {replies.map((reply, index) => (
                <div
                  key={reply.id}
                  className={`flex space-x-3 ${
                    reply.sender === 'client' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      reply.sender === 'client'
                        ? 'bg-primary-100 text-primary-900'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">
                        {reply.sender === 'client' ? 'You' : 'Support Team'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDateDisplay(reply.created_at)}
                      </span>
                    </div>
                    
                    <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                    
                    {reply.attachment && (
                      <div className="mt-2">
                        <a
                          href={`https://patrixglobal.com/${reply.attachment.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs text-primary-600 hover:text-primary-500"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          {reply.attachment.filename}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Reply Form */}
          {ticket.status !== 'Closed' && (
            <div className="mt-6 bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('dashboard.tickets.reply')}
                </h3>
              </div>
              
              <form onSubmit={handleReplySubmit} className="p-6">
                <div className="mb-4">
                  <label htmlFor="reply-message" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dashboard.tickets.message')}
                  </label>
                  <textarea
                    id="reply-message"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Type your message..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="reply-file" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('dashboard.tickets.attachFile')} (Optional)
                  </label>
                  <input
                    id="reply-file"
                    type="file"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Max file size: 10MB. Allowed: Images, PDFs, Word documents
                  </p>
                  {replyFile && (
                    <p className="mt-1 text-sm text-green-600">
                      Selected: {replyFile.name}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={replyLoading || (!replyMessage.trim() && !replyFile)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {replyLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      t('dashboard.tickets.sendReply')
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Ticket Information
            </h3>
            
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  {getStatusBadge(ticket.status)}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Request</dt>
                <dd className="mt-1 text-sm text-gray-900">{ticket.request.number}</dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDateDisplay(ticket.created_at)}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDateDisplay(ticket.updated_at)}
                </dd>
              </div>
              
              <div>
                <dt className="text-sm font-medium text-gray-500">Total Replies</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {replies.length}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;